export type BackendErrorDetail = { message?: string; error_type?: string };

type ArrayBufferErrorResponse = { response?: { data?: unknown } };

const isArrayBufferLike = (data: unknown): data is ArrayBuffer | Uint8Array => data instanceof ArrayBuffer || data instanceof Uint8Array;

/**
 * Read a FastAPI error body off a request that asked for `responseType: "arraybuffer"`.
 *
 * Axios hands such a failure's body back as an `ArrayBuffer`, so every consumer that reads
 * `error.response.data.detail` — notably `showErrorSnackbar` — sees `undefined` and falls back to a
 * generic message. A `UIVisibleError` key never reaches the user, which is worst precisely on the
 * errors that were written to explain themselves.
 *
 * Returns `undefined` when the body is not a JSON error envelope (a real binary payload, an empty
 * body, a proxy's HTML error page).
 */
export const decodeArrayBufferErrorDetail = (error: unknown): BackendErrorDetail | undefined => {
	const data = (error as ArrayBufferErrorResponse | undefined)?.response?.data;
	if (!isArrayBufferLike(data)) return undefined;

	try {
		const text = new TextDecoder().decode(data instanceof Uint8Array ? data : new Uint8Array(data));
		if (!text) return undefined;
		const parsed = JSON.parse(text) as { detail?: BackendErrorDetail | string };
		const detail = parsed.detail;
		if (!detail) return undefined;
		// FastAPI sends `detail` either as our error envelope or as a bare string message.
		return typeof detail === "string" ? { message: detail } : detail;
	} catch {
		// Not a JSON envelope — the caller keeps whatever generic message it already had.
		return undefined;
	}
};

/**
 * Decode as above, then write the result back onto the error so the existing
 * `error.response.data.detail` consumers (`showErrorSnackbar`'s `UIVisibleError` branch) work
 * unchanged. Returns the detail it found, for callers that also want to classify it.
 */
export const hydrateArrayBufferError = (error: unknown): BackendErrorDetail | undefined => {
	const detail = decodeArrayBufferErrorDetail(error);
	if (!detail) return undefined;
	const response = (error as ArrayBufferErrorResponse).response;
	if (response) response.data = { detail };
	return detail;
};
